<template>
  <main>
    <h1>{{ $t('section-titles.results') }}</h1>

    <TableWithStickyHeader
      :columns="scoreTableColumns"
      :data="scoreTableData"
    />

    <div
      v-if="!isLoading && bowlerScores.nextUrl"
      class="fetch-more-button-container"
    >
      <FormButton @click.prevent="fetchMoreScores">
        {{ $t('bowler-scores.fetch-more') }}
      </FormButton>
    </div>

    <h1 v-if="isLoading" class="busy-indicator-container">
      <BusyIndicator />
    </h1>
  </main>
</template>

<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import FormButton from '@/components/common/FormButton.vue'
import TableWithStickyHeader, {
  TableColumn,
} from '@/components/common/TableWithStickyHeader.vue'
import i18n from '@/i18n'
import { useBowlerStore } from '@/store/bowler-app/bowler'
import { useBowlerSeriesScoresStore } from '@/store/bowler-app/bowler-series-scores'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate, formatFloat, formatValue } from '@/utils/format'

const bowler = useBowlerStore()
const bowlerScores = useBowlerSeriesScoresStore()
const router = useRouter()

const errorMessage = ref<string | null>()
const isLoading = ref<boolean>(false)

const scoreTableColumns = computed<TableColumn[]>((): TableColumn[] => [
  { label: i18n.global.t('bowler-scores.date'), type: 'value' },
  { label: i18n.global.t('bowler-scores.bowling-alley'), type: '' },
  { label: i18n.global.t('bowler-scores.competitions'), type: 'wide' },
  { label: i18n.global.t('bowler-scores.score'), type: 'value' },
  { label: i18n.global.t('bowler-scores.game-count'), type: 'value-wide' },
  { label: i18n.global.t('bowler-scores.average'), type: 'value-wide' },
])

const scoreTableData = computed<string[][]>((): string[][] =>
  bowlerScores.scores.map((score) => [
    formatDate(score.date, false, true),
    formatValue(score.bowlingAlley),
    formatValue(score.competitions?.join(' · ')),
    formatFloat(score.score, 0),
    formatFloat(score.gameCount, 0),
    formatFloat(score.gameAverage, 2),
  ]),
)

const fetchScores = async (isFirstFetch = false): Promise<void> => {
  isLoading.value = true

  try {
    isFirstFetch
      ? await bowlerScores.fetch(bowler.id as number)
      : await bowlerScores.fetchNext()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(
      `Failed fetching scores for the bowler with ID ${bowler.id}: ${message}`,
    )
    errorMessage.value = message
  }

  isLoading.value = false
}

const fetchMoreScores = async (): Promise<void> => fetchScores(false)

/* hooks */

onBeforeMount(async (): Promise<void> => {
  if (isNil(bowler.id)) {
    await router.replace({ path: '/' })
  }
})

onMounted(async (): Promise<void> => fetchScores(true))
</script>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}

.fetch-more-button-container {
  min-width: 10em;
  align-self: center;
}
</style>
